// The follow-up of a website: what the team has recorded about its elements, in three parts that never stand
// in for each other (the work, the decision on each detection and the result of the last check), the
// elements one by one, and the detections discarded with their justification. Changing a state here is not
// possible: each element's state is changed from its own page, where its guide is. Nothing here starts an
// analysis or uses a page; «Comprobar la corrección» leads to the summary before the analysis.
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type CheckResult,
  type FollowItem,
  type FollowSummary,
  followUpQuery,
  MAX_CHECK_PAGES,
  type Site,
  WORK_STATUSES,
  type WorkStatus,
} from '../api';
import { buttonClass, Message, WebsiteLink } from '../ui';
import { pathOf, publicPage } from '../util';
import { ArchiveBox, CheckLink, ElementState, type Rights } from './follow-state';
import { useDate } from './scan-parts';

const CHECKS: (CheckResult | 'sinComprobar')[] = [
  'sigue-detectandose',
  'no-detectado',
  'no-comprobable',
  'requiere-revision',
  'sinComprobar',
];
const VALIDATIONS = ['pendiente', 'confirmada', 'descartada', 'sinDecidir'] as const;

/** The counts, in three blocks of their own: the work, the decisions on the detections and the checks. */
export function FollowBlocks({ summary, level = 4 }: { summary: FollowSummary; level?: 3 | 4 }) {
  const { t } = useTranslation();
  const Heading = `h${level}` as const;
  return (
    <div className="follow-blocks">
      <section aria-labelledby="follow-work">
        <Heading id="follow-work">{t('follow.work.title')}</Heading>
        <p className="text-sm text-ink-muted">{t('follow.work.description')}</p>
        <dl className="follow-counts">
          {WORK_STATUSES.map((status) => (
            <div key={status}>
              <dt>{t(`follow.work.statuses.${status}`)}</dt>
              <dd>{summary.work[status]}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-labelledby="follow-validation">
        <Heading id="follow-validation">{t('follow.validation.title')}</Heading>
        <p className="text-sm text-ink-muted">{t('follow.validation.description')}</p>
        <dl className="follow-counts">
          {VALIDATIONS.map((status) => (
            <div key={status}>
              <dt>
                {status === 'sinDecidir'
                  ? t('follow.validation.none')
                  : t(`follow.validation.statuses.${status}`)}
              </dt>
              <dd>{summary.validation[status]}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section aria-labelledby="follow-check">
        <Heading id="follow-check">{t('follow.check.title')}</Heading>
        <p className="text-sm text-ink-muted">{t('follow.check.description')}</p>
        <dl className="follow-counts">
          {CHECKS.map((result) => (
            <div key={result}>
              <dt>
                {result === 'sinComprobar' ? t('follow.check.none') : t(`follow.check.results.${result}`)}
              </dt>
              <dd>{summary.checks[result]}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

/** A card of the results with the same three blocks, and the way to the follow-up. */
export function FollowSummaryCard({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const follow = useQuery(followUpQuery(siteId));
  const summary = follow.data;
  return (
    <section className="summary-card" aria-labelledby="resumen-trabajo">
      <h3 id="resumen-trabajo">{t('follow.summary.title')}</h3>
      {follow.isPending ? (
        <p role="status">{t('app.loading')}</p>
      ) : !summary || follow.isError ? (
        <p>{t('follow.tab.loadFailed')}</p>
      ) : (
        <>
          <dl className="summary-figures">
            <div>
              <dt>{t('follow.summary.figures.tracked')}</dt>
              <dd>{summary.total}</dd>
            </div>
            <div>
              <dt>{t('follow.summary.figures.applied')}</dt>
              <dd>{summary.work.aplicada}</dd>
            </div>
            <div>
              <dt>{t('follow.summary.figures.verified')}</dt>
              <dd>{summary.checks['no-detectado']}</dd>
            </div>
          </dl>
          <p className="summary-priority-line">
            {summary.total === 0 ? t('follow.summary.none') : t('follow.summary.intro')}
          </p>
        </>
      )}
      <p className="summary-foot">
        <Link to="/sites/$siteId/seguimiento" params={{ siteId }}>
          {t('follow.summary.open')}
        </Link>{' '}
        <WebsiteLink href={`${publicPage(i18n.language, 'methodology')}#decisiones`}>
          {t('scan.discardedMethod')}
        </WebsiteLink>
      </p>
    </section>
  );
}

/** An element the team follows: its name, where it is, its three states and what can be done next. */
function Item({
  item,
  siteId,
  scanId,
  rights,
}: {
  item: FollowItem;
  siteId: string;
  scanId: string | null;
  rights: Rights;
}) {
  const { t } = useTranslation();
  const checkable = item.work.status !== 'archivada' && item.validation.status !== 'descartada';
  return (
    <li className="follow-item">
      <div className="follow-item-head">
        <p className="font-bold">
          {item.element.title}
          {item.element.zoneLabel && <span className="text-ink-muted"> — {item.element.zoneLabel}</span>}
        </p>
        <p className="text-sm text-ink-muted wrap-anywhere">
          {t('follow.tab.page')}: {pathOf(item.page.url)} ·{' '}
          {item.devices.map((device) => t(`scan.devices.${device}`)).join(', ')}
        </p>
      </div>
      {/* Two states told apart: what the team declares, and what a check verified. */}
      <p className="follow-chips">
        <span className="origin-chip">
          {t('follow.quick.work')}: {t(`follow.work.statuses.${item.work.status}`)}
        </span>
        <span className="origin-chip" data-verified={item.check?.result === 'no-detectado' || undefined}>
          {t('follow.quick.verification')}:{' '}
          {item.check ? t(`follow.check.results.${item.check.result}`) : t('follow.check.none')}
        </span>
      </p>
      <div className="follow-item-actions">
        {scanId && (
          <Link
            to="/sites/$siteId/paginas"
            params={{ siteId }}
            search={{ scan: scanId, p: item.page.key, e: item.locatedAs ?? undefined }}
            className={buttonClass('secondary')}
          >
            {item.locatedAs ? t('follow.tab.open') : t('follow.tab.openPage')}
          </Link>
        )}
        {checkable && <CheckLink siteId={siteId} ids={[item.id]} />}
      </div>
      {!item.locatedAs && scanId && <p className="text-sm text-ink-muted">{t('follow.tab.noCapture')}</p>}
      <ArchiveBox scanId={scanId ?? ''} siteId={siteId} state={item} mayTrack={rights.track} />
      <details className="issue-details">
        <summary className="cursor-pointer font-bold">{t('follow.tab.manage')}</summary>
        <ElementState
          scanId={scanId ?? ''}
          siteId={siteId}
          element={{ row: item.id }}
          state={item}
          result={item.result}
          devices={item.devices}
          mayTrack={rights.track}
          mayDiscard={rights.discard}
          headingLevel={6}
          inList
          pageKey={item.page.key}
        />
      </details>
    </li>
  );
}

type WorkFilter = 'todos' | WorkStatus;
type CheckFilter = 'todos' | CheckResult | 'sinComprobar';

/** The follow-up tab of a website. */
export function FollowSection({ site, rights }: { site: Site; rights: Rights }) {
  const { t } = useTranslation();
  const date = useDate();
  const follow = useQuery(followUpQuery(site.id));
  const [work, setWork] = useState<WorkFilter>('todos');
  const [check, setCheck] = useState<CheckFilter>('todos');
  const [archived, setArchived] = useState(false);
  const summary = follow.data;

  if (follow.isPending) return <p role="status">{t('app.loading')}</p>;
  if (follow.isError || !summary) return <Message tone="error">{t('follow.tab.loadFailed')}</Message>;

  const live = summary.items.filter((item) => item.validation.status !== 'descartada');
  const discarded = summary.items.filter((item) => item.validation.status === 'descartada');
  const hiddenArchived = live.filter((item) => item.work.status === 'archivada').length;
  const shown = live.filter(
    (item) =>
      (work === 'todos' ? archived || item.work.status !== 'archivada' : item.work.status === work) &&
      (check === 'todos' || (check === 'sinComprobar' ? !item.check : item.check?.result === check)),
  );
  // The fixes the team says are in place: the ones worth checking together.
  const applied = live.filter((item) => item.work.status === 'aplicada');
  const appliedPages = new Set(applied.map((item) => item.page.url)).size;

  return (
    <section className="follow-tab" aria-labelledby="seguimiento">
      <h2 id="seguimiento" className="issues-title">
        {t('follow.tab.title')}
      </h2>
      <p className="text-ink-muted">{t('follow.tab.intro')}</p>
      {summary.total === 0 ? (
        <div className="empty-panel">
          <p>{t('follow.tab.empty')}</p>
          <Link
            to={summary.scanId ? '/sites/$siteId/scans/$scanId' : '/sites/$siteId'}
            params={summary.scanId ? { siteId: site.id, scanId: summary.scanId } : { siteId: site.id }}
            className={buttonClass('primary')}
          >
            {t('follow.tab.viewProblems')}
          </Link>
        </div>
      ) : (
        <>
          <dl className="summary-figure-cards follow-figures">
            <div>
              <dt>{t('follow.work.statuses.pendiente')}</dt>
              <dd>
                <strong>{summary.work.pendiente}</strong>
              </dd>
            </div>
            <div>
              <dt>{t('follow.work.statuses.en-curso')}</dt>
              <dd>
                <strong>{summary.work['en-curso']}</strong>
              </dd>
            </div>
            <div>
              <dt>{t('follow.summary.figures.applied')}</dt>
              <dd>
                <strong>{summary.work.aplicada}</strong>
              </dd>
            </div>
            <div>
              <dt>{t('follow.summary.figures.verified')}</dt>
              <dd>
                <strong>{summary.checks['no-detectado']}</strong>
              </dd>
            </div>
          </dl>
          {applied.length > 0 && (
            <div className="follow-verify">
              {appliedPages <= MAX_CHECK_PAGES ? (
                <>
                  <CheckLink
                    siteId={site.id}
                    ids={applied.map((item) => item.id)}
                    label={t('follow.verify.many', { count: applied.length })}
                    variant="primary"
                  />
                </>
              ) : (
                <p>{t('follow.tab.checkTooMany', { max: MAX_CHECK_PAGES })}</p>
              )}
            </div>
          )}

          <fieldset className="results-filters">
            <legend className="sr-only">{t('follow.tab.filters')}</legend>
            <div className="follow-filters">
              <label className="page-sort" htmlFor="filtro-trabajo">
                <span>{t('follow.tab.work')}</span>
                <select
                  id="filtro-trabajo"
                  value={work}
                  onChange={(event) => setWork(event.target.value as WorkFilter)}
                  className="page-sort-select min-h-11 w-full rounded-md border border-field-border py-2 text-ink"
                >
                  {(['todos', ...WORK_STATUSES] as WorkFilter[]).map((value) => (
                    <option key={value} value={value}>
                      {value === 'todos' ? t('follow.tab.all') : t(`follow.work.statuses.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="page-sort" htmlFor="filtro-comprobacion">
                <span>{t('follow.tab.check')}</span>
                <select
                  id="filtro-comprobacion"
                  value={check}
                  onChange={(event) => setCheck(event.target.value as CheckFilter)}
                  className="page-sort-select min-h-11 w-full rounded-md border border-field-border py-2 text-ink"
                >
                  {(['todos', ...CHECKS] as CheckFilter[]).map((value) => (
                    <option key={value} value={value}>
                      {value === 'todos'
                        ? t('follow.tab.all')
                        : value === 'sinComprobar'
                          ? t('follow.check.none')
                          : t(`follow.check.results.${value}`)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {work === 'todos' && hiddenArchived > 0 && (
              <label className="follow-check">
                <input
                  type="checkbox"
                  checked={archived}
                  onChange={(event) => setArchived(event.target.checked)}
                />
                {t('follow.tab.showArchived')} ({hiddenArchived})
              </label>
            )}
            <p role="status" className="text-sm">
              {t('follow.tab.shown', { count: shown.length, shown: shown.length, total: live.length })}
            </p>
          </fieldset>

          {shown.length === 0 ? (
            <p>{t('follow.tab.none')}</p>
          ) : (
            <ul className="follow-list">
              {shown.map((item) => (
                <Item key={item.id} item={item} siteId={site.id} scanId={summary.scanId} rights={rights} />
              ))}
            </ul>
          )}

          {discarded.length > 0 && (
            <section aria-labelledby="descartes" className="follow-discards">
              <h3 id="descartes">{t('follow.tab.discards')}</h3>
              <ul className="follow-list">
                {discarded.map((item) => (
                  <li key={item.id} className="follow-item">
                    <p className="font-bold">
                      {item.element.title} — {item.ruleTitle}
                    </p>
                    <p className="text-sm text-ink-muted wrap-anywhere">{pathOf(item.page.url)}</p>
                    <p>
                      {t('follow.validation.justificationLine', {
                        text: item.validation.justification ?? '',
                      })}
                    </p>
                    <p>{t('follow.validation.evidenceLine', { text: item.validation.evidence ?? '' })}</p>
                    <p className="text-sm">
                      {item.validation.at &&
                        t('follow.tab.discardedBy', {
                          name: item.validation.by?.name || t('follow.someone'),
                          date: date(item.validation.at, 'short'),
                        })}
                    </p>
                    <div className="follow-item-actions">
                      {summary.scanId && (
                        <Link
                          to="/sites/$siteId/paginas"
                          params={{ siteId: site.id }}
                          search={{ scan: summary.scanId, p: item.page.key, e: item.locatedAs ?? undefined }}
                          className={buttonClass('secondary')}
                        >
                          {item.locatedAs ? t('follow.tab.open') : t('follow.tab.openPage')}
                        </Link>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </section>
  );
}
